import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, Link2, Check } from 'lucide-react';
import { toast } from 'sonner';

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}><path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.6c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.7-.3zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2z" /></svg>
);
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.3v7A10 10 0 0 0 22 12z" /></svg>
);

// Generic one-tap share (WhatsApp, Facebook, copy link, native share). `src` tags are appended for share stats.
export const ShareButtons = ({ url: baseUrl, title, text, testId, compact = false }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const withSrc = (src) => `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}src=${src}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(withSrc('link')); setCopied(true); toast.success(t('share.copied')); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error(t('share.copyError')); }
  };
  const native = async () => {
    try { await navigator.share({ title, text, url: withSrc('native') }); } catch { /* cancelled */ }
  };

  const btn = 'inline-flex items-center gap-1.5 rounded-full border text-sm font-semibold px-3 h-9 transition-colors';
  return (
    <div className={`flex flex-wrap items-center gap-2 ${compact ? '' : 'mt-2'}`} data-testid={testId}>
      {!compact && <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mr-1 flex items-center gap-1"><Share2 className="h-3.5 w-3.5" />{t('share.label')}</span>}
      <a href={`https://wa.me/?text=${encodeURIComponent(`${text} ${withSrc('wa')}`)}`} target="_blank" rel="noopener noreferrer" className={`${btn} border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100`} data-testid={`${testId}-whatsapp`}>
        <WhatsAppIcon className="h-4 w-4" />WhatsApp
      </a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(withSrc('fb'))}`} target="_blank" rel="noopener noreferrer" className={`${btn} border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100`} data-testid={`${testId}-facebook`}>
        <FacebookIcon className="h-4 w-4" />Facebook
      </a>
      <button type="button" onClick={copy} className={`${btn} border-slate-200 bg-white text-slate-700 hover:bg-slate-50`} data-testid={`${testId}-copy`}>
        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Link2 className="h-4 w-4" />}{copied ? t('share.copiedShort') : t('share.copy')}
      </button>
      {typeof navigator !== 'undefined' && navigator.share && (
        <button type="button" onClick={native} className={`${btn} border-slate-200 bg-white text-slate-700 hover:bg-slate-50`} data-testid={`${testId}-native`}>
          <Share2 className="h-4 w-4" />{t('share.more')}
        </button>
      )}
    </div>
  );
};

// Job-specific wrapper (keeps the share-job-<id> / share-whatsapp-<id> test ids)
export const ShareJobButtons = ({ jobId, title, company, compact = false }) => {
  const { t } = useTranslation();
  const url = `${window.location.origin}/jobs/${jobId}`;
  const text = t('share.text', { title, company: company || 'Trabadju.cv' });
  return <ShareButtons url={url} title={title} text={text} compact={compact} testId={`share-job-${jobId}`} />;
};
